import { copyDrawing, emptyDrawing } from '../../shared/art/drawing';
import type { Drawing } from '../../shared/art/types';
import type { LiveDrawingOperation } from './types';
import { continuesMirror } from './drawing-history';

/** The canvas follows the same ordered operations as the server, including whole-document undo. */
export class LiveCanvasModel {
  drawing: Drawing = emptyDrawing();
  revision = 0;
  private undo: Drawing[] = [];
  private redo: Drawing[] = [];
  private undoCount = 0;
  private redoCount = 0;
  get canUndo() { return this.undoCount > 0; }
  get canRedo() { return this.redoCount > 0; }
  reset(drawing: Drawing, history?: { undo: number; redo: number }) {
    this.drawing = copyDrawing(drawing); this.undo = []; this.redo = [];
    this.undoCount = history?.undo ?? drawing.strokes.length; this.redoCount = history?.redo ?? 0; this.revision++;
  }
  apply(operation: LiveDrawingOperation) {
    const history = () => { this.undo.push(copyDrawing(this.drawing)); if (this.undo.length > 30) this.undo.shift(); this.redo = []; this.undoCount = Math.min(30, this.undoCount + 1); this.redoCount = 0; };
    const d = this.drawing;
    if (operation.kind === 'begin') {
      if (!continuesMirror(d, operation.stroke)) history();
      const stroke = JSON.parse(JSON.stringify(operation.stroke)); d.strokes.push(stroke);
      (d.events ??= []).push({ kind: 'stroke', t: stroke.points[0].t, stroke });
    } else if (operation.kind === 'append') {
      const stroke = d.strokes.at(-1); if (!stroke) return;
      if (stroke.tool === 'line' || stroke.tool === 'shape') stroke.points = [stroke.points[0], operation.points.at(-1)!];
      else stroke.points.push(...operation.points.map(point => ({ ...point })));
      const event = d.events?.at(-1); if (event?.kind === 'stroke') event.stroke = stroke;
    } else if (operation.kind === 'clear') { history(); this.drawing = { schemaVersion: 1, strokes: [], events: [...(d.events ?? []), { kind: 'clear', t: d.events?.at(-1)?.t ?? 0 }] }; }
    else if (operation.kind === 'fill') { history(); d.background = operation.background; (d.events ??= []).push({ kind: 'fill', t: d.events?.at(-1)?.t ?? 0, background: operation.background }); }
    else if (operation.kind === 'undo') { if (this.canUndo) { this.undoCount--; this.redoCount++; } const previous = this.undo.pop(); if (previous) { this.redo.push(copyDrawing(d)); this.drawing = previous; } }
    else if (operation.kind === 'redo') { if (this.canRedo) { this.redoCount--; this.undoCount++; } const next = this.redo.pop(); if (next) { this.undo.push(copyDrawing(d)); this.drawing = next; } }
    this.revision++;
  }
}

export function liveBoardLayout(height: number, keyboardHeight = 0, artist = false, width = 390, tablet = false) {
  if (tablet) {
    const available = Math.max(320, height - keyboardHeight), seatHeight = keyboardHeight ? 70 : 104;
    const seatsY = available - seatHeight * 2 - (keyboardHeight ? 12 : 68);
    const titleY = keyboardHeight ? 86 : 100, y = titleY + 28;
    const toolsHeight = keyboardHeight ? 44 : artist ? 168 : 232;
    const size = Math.max(40, Math.min(width - 64, seatsY - y - toolsHeight - 30));
    return {x:(width-size)/2,y,size,seatsY,seatHeight,titleY,toolsHeight,toolsY:y+size+14,footerY:available-28,slotH:44,tileH:44,controlH:44};
  }
  const available = Math.max(280, height - keyboardHeight);
  const compact = height < 680 || keyboardHeight > 0;
  const short = height < 560;
  const slotH = short ? 28 : compact ? 30 : 36;
  const tileH = short ? 24 : compact ? 26 : 32;
  const seatHeight = keyboardHeight || short ? 54 : compact ? 60 : 74;
  const seatsY = available - seatHeight * 2 - (keyboardHeight ? 8 : short ? 60 : 66);
  const titleY = keyboardHeight ? 70 : short ? 86 : 94;
  const y = titleY + 24;
  if (keyboardHeight) {
    const size = Math.max(26, Math.min(220, seatsY - y - 50));
    return { x: (390 - size) / 2, y, size, seatsY, seatHeight, titleY, toolsHeight: 38, toolsY: y + size + 6, footerY: available - 12, slotH, tileH, controlH: 34 };
  }
  const toolsHeight = artist ? 150 : slotH + tileH * 2 + 88;
  const size = Math.max(54, Math.min(330, seatsY - y - (artist ? 24 : 14) - toolsHeight));
  return { x: (390 - size) / 2, y, size, seatsY, seatHeight, titleY, toolsHeight, toolsY: y + size + 10, footerY: available - 28, slotH, tileH, controlH: compact ? 28 : 32 };
}
